"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Link2, Loader2 } from "lucide-react";

interface CrossReference {
  target_verse_id: string;
  score: number;
  book: string;
  chapter: number;
  verse: number;
  text: string;
  translation?: string;
}

interface CrossReferencesProps {
  book: string;
  chapter: number;
  verse: number;
}

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api";

export default function CrossReferences({
  book,
  chapter,
  verse,
}: CrossReferencesProps) {
  const [references, setReferences] = useState<CrossReference[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    const bookSlug = book.toLowerCase().replace(/\s+/g, "_");
    const verseId = `kjv-${bookSlug}-${chapter}-${verse}`;

    setLoading(true);

    fetch(`${API_BASE}/crossrefs/${encodeURIComponent(verseId)}`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Cross-reference request failed: ${response.status}`);
        }

        return response.json();
      })
      .then((data) => {
        setReferences(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error(error);
          setReferences([]);
        }
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [book, chapter, verse]);

  return (
    <div className="mb-4">
      <div
        className="flex items-center gap-2 mb-2 text-xs font-semibold"
        style={{ color: "var(--text-secondary)" }}
      >
        <Link2 size={13} />
        Related verses
      </div>

      {loading ? (
        <div
          className="flex items-center gap-2 text-xs"
          style={{ color: "var(--text-secondary)" }}
        >
          <Loader2 size={12} className="animate-spin" />
          Loading references...
        </div>
      ) : references.length === 0 ? (
        <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
          No related verses found.
        </p>
      ) : (
        <div className="space-y-2">
          {references.slice(0, 5).map((reference) => (
            <Link
              key={reference.target_verse_id}
              href={`/reader/${encodeURIComponent(reference.book)}/${reference.chapter}`}
              className="block rounded-lg p-2 transition-colors hover:bg-white/5"
              style={{
                border: "1px solid rgba(200, 164, 94, 0.12)",
              }}
            >
              <div
                className="text-xs font-semibold mb-1"
                style={{ color: "var(--accent-gold)" }}
              >
                {reference.book} {reference.chapter}:{reference.verse}
              </div>

              <p
                className="text-xs leading-relaxed line-clamp-3"
                style={{ color: "var(--text-secondary)" }}
              >
                {reference.text}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
